import type { LabeledEdgeProps } from "@/pages/canvas/types/canvasNodeData";
import { getFloatingEdgeParams } from "@/pages/canvas/utils/floatingEdge";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useInternalNode,
} from "@xyflow/react";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { LineLabelEditor } from "./shared/LineLabel";

export type {
  CanvasLabeledEdge,
  LabeledEdgeData,
} from "@/pages/canvas/types/canvasNodeData";

/**
 * Custom RF edge type: renders a bezier/step/straight path plus an optional
 * label at the path midpoint (React Flow's native `label` field; its
 * typography rides in `data.label_style`). Click the label to edit it;
 * double-click the edge (see CanvasPage's onEdgeDoubleClick) or the bar's
 * Text button adds a label to an unlabeled edge. While the label is being
 * edited the edge bar yields to the label bar.
 * Registered as `edgeTypes={{ labeled: LabeledEdge }}` in CanvasPage.
 */
export function LabeledEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerStart,
  markerEnd,
  style,
  label,
}: LabeledEdgeProps) {
  const edgeData = data ?? {};
  const curveType = edgeData.curve_type ?? "step";

  // Figma-style floating endpoints: re-derive the attachment point/side from
  // the live node geometry so dragging never leaves an awkward opposing-handle
  // connection. Subscribes to the internal node store, so it recomputes on
  // every drag frame. Falls back to RF's handle-derived coords when a node
  // isn't measured yet (initial render).
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const floating = getFloatingEdgeParams(sourceNode, targetNode);

  const sx = floating?.sx ?? sourceX;
  const sy = floating?.sy ?? sourceY;
  const tx = floating?.tx ?? targetX;
  const ty = floating?.ty ?? targetY;
  const sPos = floating?.sourcePos ?? sourcePosition;
  const tPos = floating?.targetPos ?? targetPosition;

  const straightArgs = { sourceX: sx, sourceY: sy, targetX: tx, targetY: ty };
  const curvedArgs = {
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sPos,
    targetX: tx,
    targetY: ty,
    targetPosition: tPos,
  };

  const [edgePath, labelX, labelY] =
    curveType === "straight"
      ? getStraightPath(straightArgs)
      : curveType === "step"
        ? getSmoothStepPath(curvedArgs)
        : getBezierPath(curvedArgs);

  const { canEdit, updateEdgeLabel } = useCanvasApi();
  const { editingEdgeId, setEditingEdgeId } = useCanvasUi();
  const isEditing = editingEdgeId === id;
  const labelText = typeof label === "string" ? label : "";

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={style}
      />
      {(labelText || isEditing) && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
            }}
          >
            <LineLabelEditor
              label={labelText}
              labelStyle={edgeData.label_style}
              editing={isEditing}
              canEdit={canEdit}
              midpoint={{ x: labelX, y: labelY }}
              onStartEdit={() => setEditingEdgeId(id)}
              onEndEdit={() => setEditingEdgeId(null)}
              onCommit={(text) => updateEdgeLabel(id, { label: text })}
              onStyleChange={(label_style) =>
                updateEdgeLabel(id, { label_style })
              }
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
